<!--
  Spostamento interno — dettaglio.

  Uno spostamento interno non è un booking: altra tabella, altre relazioni
  (journey → routes, con driver e jolly DIRETTAMENTE sulla tratta, non su
  planner). Stesso impianto visivo del dettaglio prenotazione: tratte a
  sinistra, riepilogo sticky a destra.
-->
<template>
  <div
    v-loading="loading"
    class="jdt"
  >
    <div class="jdt__head">
      <button
        type="button"
        class="jdt__back"
        @click="goBack"
      >
        ‹
      </button>
      <div class="jdt__ident">
        <h1 class="jdt__title">
          Spostamento interno
          <StatusPill
            :kind="dayState.kind"
            :label="dayState.label"
          />
        </h1>
        <div class="jdt__sub">
          <ServiceChip service="journey" />
          <span class="jdt__id">#{{ shortId }}</span>
          <span v-if="journey.date">· {{ dLabel(journey.date) }}</span>
          <span v-if="partnerName">· {{ partnerName }}</span>
        </div>
      </div>
      <div class="jdt__spacer" />
      <el-button
        v-if="!giornata.aperta && routes.length"
        @click="apriModificaGiornata"
      >
        Modifica spostamento
      </el-button>
    </div>

    <div class="jdt__grid">
      <!-- ================= TRATTE ================= -->
      <div class="jdt__col">
        <!-- MODIFICA DELLO SPOSTAMENTO: lo stesso modulo del partner (corse in
             mezzo, più auto, rientri, tipo e sedi). Le corse già salvate
             restano le stesse — driver, foto, minuti, rimborsi, importi — e
             cambia solo quello che si tocca. Niente scadenza per Simba. -->
        <section
          v-if="giornata.aperta"
          class="jdt__card"
        >
          <header class="jdt__cardHead">
            <h3>Modifica spostamento</h3>
          </header>
          <div class="jdt__cardBody">
            <journey-legs-editor
              :key="giornata.sessione"
              ref="legsEditor"
              :initial-routes="giornata.corse"
              :date="giornoIso"
              :journey-id="id"
              :headquarters="sedi"
              :partner-id="journey.partner?.id || journey.partnerId || ''"
              @route-removed="(sid) => giornata.daEliminare.push(sid)"
            />
            <div class="jdt__editActions">
              <el-checkbox v-model="giornata.forza">
                Salva anche le corse non disponibili
              </el-checkbox>
              <div class="jdt__spacer" />
              <el-button @click="chiudiModificaGiornata">
                Annulla
              </el-button>
              <el-button
                type="primary"
                :loading="giornata.salvando"
                @click="salvaModificaGiornata"
              >
                Salva modifiche
              </el-button>
            </div>
          </div>
        </section>

        <section
          v-for="(r, i) in (giornata.aperta ? [] : routes)"
          :key="r.id"
          class="jdt__card"
        >
          <!-- Testata: chi è la tratta (numero, tipo, orario) e il suo stato.
               Lo stato si cambia dal menu della pastiglia; eliminare ha il
               suo pulsante perché avvisa il driver. -->
          <header class="jdt__cardHead">
            <h3>Tratta {{ i + 1 }}</h3>
            <span class="jdt__tag2op">{{ r.two_operators === false ? 'A catena' : 'Singolo' }}</span>
            <div class="jdt__spacer" />
            <el-dropdown
              trigger="click"
              :disabled="statusRoute === r.id"
              @command="(v) => cambiaStato(r, v)"
            >
              <button
                type="button"
                class="jdt__stateBtn"
                :aria-label="`Stato della tratta ${i + 1}: ${statePill(r).label}. Cambia`"
              >
                <StatusPill
                  :kind="statePill(r).kind"
                  :label="statePill(r).label"
                />
                <span
                  class="jdt__caret"
                  aria-hidden="true"
                >▾</span>
              </button>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item
                    v-for="o in ROUTE_STATES"
                    :key="o.value"
                    :command="o.value"
                    :disabled="o.value === currentState(r)"
                  >
                    {{ o.label }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
            <el-button
              size="small"
              text
              :disabled="r.status === 'CONCLUSA'"
              :title="r.status === 'CONCLUSA' ? 'Tratta conclusa: riaprila (stato) per modificarla' : undefined"
              @click="apriModifica(r, i)"
            >
              Modifica
            </el-button>
            <el-button
              size="small"
              text
              type="danger"
              :loading="statusRoute === r.id && deletingRoute"
              @click="deleteRoute(r)"
            >
              Elimina
            </el-button>
          </header>

          <div class="jdt__cardBody">
            <!-- Il viaggio a sinistra, come rientra il driver a destra. -->
            <div class="jdt__overview">
              <div class="jdt__trip">
                <!-- Ogni orario accanto al suo indirizzo. -->
                <div class="jdt__route">
                  <span class="jdt__time">{{ hhmm(r.dateStart) }}</span>
                  <span class="jdt__pin jdt__pin--from" />
                  <span class="jdt__addr">{{ place(r.startPoint) || '—' }}</span>
                  <span class="jdt__time jdt__time--arr">{{ hhmm(r.dateEnd) }}</span>
                  <span class="jdt__pin jdt__pin--to" />
                  <span class="jdt__addr">{{ place(r.endPoint) || '—' }}</span>
                  <!-- I dipendenti del partner della sede d'arrivo: se il
                       driver arriva e non trova nessuno, si chiama loro. -->
                  <template v-if="referentiSede(r.end_headquarter_id)">
                    <span />
                    <span />
                    <span
                      class="jdt__who"
                      title="Dipendenti del partner in questa sede: se il driver arriva e non trova nessuno, si chiama loro"
                    >Referente in sede: {{ referentiSede(r.end_headquarter_id) }}</span>
                  </template>
                </div>
                <div class="jdt__veh">
                  <span
                    v-if="r.targa"
                    class="jdt__vehPlate"
                  >{{ r.targa }}</span>
                  <!-- Come nella lista /booking: accanto alla targa. -->
                  <span
                    v-if="r.immatricolato === false"
                    class="jdt__prova"
                    title="Veicolo non immatricolato: si sposta con la targa prova"
                  >Targa prova</span>
                  <span class="jdt__vehCar">{{ [r.marca, r.modello].filter(Boolean).join(' ') || 'Veicolo' }}</span>
                  <span
                    v-if="r.gearbox_type"
                    class="jdt__dim"
                  >· cambio {{ r.gearbox_type === 'AUTOMATIC' ? 'automatico' : 'manuale' }}</span>
                </div>
                <div class="jdt__facts">
                  <span v-if="r.km">{{ r.km }} km</span>
                  <span v-if="r.min">{{ Math.round(Number(r.min)) }} min</span>
                  <span>inserita da {{ personName(routeAuthors[r.id]) || 'Simba' }}</span>
                </div>
              </div>

              <!-- Come rientra il driver, in schema: come, con cosa, e chi
                   porta a bordo. -->
              <dl
                class="jdt__rientro"
                :class="{ 'jdt__rientro--attn': rientroDi(r).attn }"
              >
                <dt class="jdt__k">
                  Rientro del driver
                </dt>
                <dd class="jdt__rientroCome">
                  {{ rientroDi(r).come }}
                </dd>
                <dd
                  v-if="rientroDi(r).dove"
                  class="jdt__rientroDove"
                >
                  {{ rientroDi(r).dove }}
                </dd>
                <template v-if="rientroDi(r).arriva">
                  <dt class="jdt__k jdt__k--sub">
                    Arriva a bordo
                  </dt>
                  <dd class="jdt__rientroDove">
                    {{ rientroDi(r).arriva }}
                  </dd>
                </template>
                <template v-if="rientroDi(r).vanno">
                  <dt class="jdt__k jdt__k--sub">
                    Porta all'andata
                  </dt>
                  <dd class="jdt__rientroDove">
                    {{ rientroDi(r).vanno }}
                  </dd>
                </template>
                <template v-if="rientroDi(r).aBordo">
                  <dt class="jdt__k jdt__k--sub">
                    {{ r.two_operators !== false ? 'Riporta anche' : 'Porta a bordo' }}
                  </dt>
                  <dd class="jdt__rientroDove">
                    {{ rientroDi(r).aBordo }}
                  </dd>
                </template>
              </dl>
            </div>

            <!-- NOTE della tratta: si leggono e si scrivono qui (partner o
                 Simba, è lo stesso campo). Salvarle non cambia altro. -->
            <div class="jdt__notes">
              <label
                class="jdt__k"
                :for="`note-${r.id}`"
              >Note</label>
              <el-input
                :id="`note-${r.id}`"
                v-model="note[r.id]"
                type="textarea"
                :autosize="{ minRows: 1, maxRows: 12 }"
                maxlength="255"
                placeholder="Nessuna nota: scrivi qui per aggiungerne una"
                class="jdt__notesIn"
              />
              <div
                v-if="(note[r.id] || '').trim() !== (r.details || '')"
                class="jdt__notesAct"
              >
                <el-button
                  size="small"
                  text
                  @click="note[r.id] = r.details || ''"
                >
                  Annulla
                </el-button>
                <el-button
                  size="small"
                  type="primary"
                  :loading="savingNote === r.id"
                  @click="salvaNota(r)"
                >
                  Salva nota
                </el-button>
              </div>
            </div>

            <!-- Il lavoro di Simba sulla tratta, una cosa alla volta. -->
            <el-tabs
              v-model="schede[r.id]"
              class="jdt__tabs"
            >
              <el-tab-pane
                name="equipaggio"
                :label="r.two_operators !== false ? 'Driver e jolly' : conPasseggeri(r) ? 'Conducente e passeggeri' : eRitorno(r) ? 'Driver e passeggeri' : 'Driver'"
              >
                <div class="jdt__form">
                  <!-- Ritorno con passeggeri: chi guida è il CONDUCENTE (nell'app
                       lo vede da jolly, i passeggeri da driver). -->
                  <span class="jdt__plRole">{{ conPasseggeri(r) ? 'Conducente' : 'Driver' }}</span>
                  <div class="jdt__field">
                    <el-select
                      v-model="drafts[r.id].driverId"
                      placeholder="Non assegnato"
                      filterable
                      clearable
                      size="small"
                      class="jdt__plSel"
                      @change="(v) => compilaPasseggeri(r, v)"
                    >
                      <el-option
                        v-for="d in drivers"
                        :key="d.id"
                        :label="`${d.name} ${d.surname}`"
                        :value="d.id"
                      />
                    </el-select>
                    <el-input
                      v-model="drafts[r.id].extraMinDriver"
                      size="small"
                      class="jdt__plMin"
                      placeholder="0"
                      aria-label="Minuti aggiuntivi del driver"
                    >
                      <template #append>
                        min agg.
                      </template>
                    </el-input>
                  </div>

                  <!-- SINGOLO: il jolly riporta il driver, oppure Simba decide
                       che rientra con i mezzi pubblici (niente jolly). -->
                  <template v-if="r.two_operators !== false">
                    <span class="jdt__plRole">Rientro</span>
                    <div class="jdt__field">
                      <el-radio-group
                        v-model="drafts[r.id].rientroAltrove"
                        size="small"
                      >
                        <el-radio-button :label="false">
                          Con il jolly
                        </el-radio-button>
                        <el-radio-button :label="true">
                          Mezzi pubblici
                        </el-radio-button>
                      </el-radio-group>
                    </div>
                    <template v-if="!drafts[r.id].rientroAltrove">
                      <span class="jdt__plRole jdt__plRole--jolly">Jolly</span>
                      <div class="jdt__field">
                        <el-select
                          v-model="drafts[r.id].jollyDriverId"
                          placeholder="Da assegnare"
                          filterable
                          clearable
                          size="small"
                          class="jdt__plSel"
                        >
                          <el-option
                            v-for="d in drivers"
                            :key="d.id"
                            :label="`${d.name} ${d.surname}`"
                            :value="d.id"
                          />
                        </el-select>
                        <el-input
                          v-model="drafts[r.id].extraMinJolly"
                          size="small"
                          class="jdt__plMin"
                          placeholder="0"
                          aria-label="Minuti aggiuntivi del jolly"
                        >
                          <template #append>
                            min agg.
                          </template>
                        </el-input>
                      </div>
                    </template>
                    <p
                      v-else
                      class="jdt__hint jdt__span"
                    >
                      Niente jolly: la tratta si conferma col solo driver, il biglietto si rimborsa come trasferta.
                    </p>
                    <!-- Chi rientra col jolly di questo singolo (driver di altre
                         tratte a catena arrivate dove arriva lui). -->
                    <template v-if="passeggeriSingolo(r)">
                      <span class="jdt__plRole jdt__plRole--jolly">Col jolly</span>
                      <div class="jdt__field">
                        <el-select
                          v-model="drafts[r.id].passengerIds"
                          multiple
                          :multiple-limit="4"
                          placeholder="Nessuno (fino a 4)"
                          filterable
                          clearable
                          size="small"
                          class="jdt__plSel"
                        >
                          <el-option
                            v-for="d in drivers.filter((x) => x.id !== drafts[r.id].driverId && x.id !== drafts[r.id].jollyDriverId)"
                            :key="d.id"
                            :label="`${d.name} ${d.surname}`"
                            :value="d.id"
                          />
                        </el-select>
                      </div>
                      <p
                        v-if="daChiRientra(r).length"
                        class="jdt__hint jdt__span"
                      >
                        Rientrano col jolly i driver {{ daChiRientra(r).length > 1 ? 'delle tratte' : 'della tratta' }}
                        {{ daChiRientra(r).join(', ') }}: si compilano da soli assegnando il driver a quella tratta.
                      </p>
                    </template>
                  </template>

                  <!-- RITORNO A CATENA: i passeggeri, fino a 4. -->
                  <template v-else-if="eRitorno(r)">
                    <span class="jdt__plRole jdt__plRole--jolly">Passeggeri</span>
                    <div class="jdt__field">
                      <el-select
                        v-model="drafts[r.id].passengerIds"
                        multiple
                        :multiple-limit="4"
                        placeholder="Nessuno (fino a 4)"
                        filterable
                        clearable
                        size="small"
                        class="jdt__plSel"
                      >
                        <el-option
                          v-for="d in drivers.filter((x) => x.id !== drafts[r.id].driverId)"
                          :key="d.id"
                          :label="`${d.name} ${d.surname}`"
                          :value="d.id"
                        />
                      </el-select>
                    </div>
                    <p
                      v-if="chiArriva(r).length"
                      class="jdt__hint jdt__span"
                    >
                      All'andata viaggiano a bordo i driver {{ chiArriva(r).length > 1 ? 'delle tratte' : 'della tratta' }}
                      {{ chiArriva(r).join(', ') }}: si compilano da soli assegnando il driver a quella tratta.
                    </p>
                    <p
                      v-if="daChiRientra(r).length"
                      class="jdt__hint jdt__span"
                    >
                      Scegliendo il conducente si compilano con gli altri driver arrivati
                      (tratte {{ daChiRientra(r).join(', ') }} e quella da cui riparte); puoi correggerli.
                    </p>
                  </template>

                  <div class="jdt__span jdt__actions">
                    <el-button
                      size="small"
                      type="primary"
                      :loading="savingRoute === r.id"
                      @click="saveAssignment(r)"
                    >
                      Salva
                    </el-button>
                  </div>
                </div>
              </el-tab-pane>

              <!-- RIMBORSI: gli stessi del COD6, per chi è assegnato. -->
              <el-tab-pane
                name="rimborsi"
                label="Rimborsi"
              >
                <p
                  v-if="!r.driver && !r.jolly_driver"
                  class="jdt__hint"
                >
                  Si scrivono quando la tratta ha il driver assegnato.
                </p>
                <div
                  v-else
                  class="jdt__form"
                >
                  <template v-if="r.driver">
                    <span class="jdt__plRole">Trasferta driver</span>
                    <div class="jdt__field">
                      <el-input
                        v-model="rimborsi[r.id].trasfertaDriver"
                        size="small"
                        class="jdt__plMin"
                        placeholder="€ 0"
                        aria-label="Rimborso trasferta del driver"
                      />
                      <el-checkbox
                        v-model="rimborsi[r.id].trasfertaDriverPagata"
                        :disabled="!rimborsi[r.id].trasfertaDriver"
                        size="small"
                      >
                        pagata
                      </el-checkbox>
                    </div>
                  </template>
                  <template v-if="r.jolly_driver">
                    <span class="jdt__plRole jdt__plRole--jolly">Trasferta jolly</span>
                    <div class="jdt__field">
                      <el-input
                        v-model="rimborsi[r.id].trasfertaJolly"
                        size="small"
                        class="jdt__plMin"
                        placeholder="€ 0"
                        aria-label="Rimborso trasferta del jolly"
                      />
                      <el-checkbox
                        v-model="rimborsi[r.id].trasfertaJollyPagata"
                        :disabled="!rimborsi[r.id].trasfertaJolly"
                        size="small"
                      >
                        pagata
                      </el-checkbox>
                    </div>
                    <span class="jdt__plRole jdt__plRole--jolly">Rimb. km jolly</span>
                    <div class="jdt__field">
                      <el-input
                        v-model="rimborsi[r.id].rimborsoKm"
                        size="small"
                        class="jdt__plKm"
                        :placeholder="rimborsoKmCalcolato(r)"
                        aria-label="Rimborso km del jolly"
                      />
                    </div>
                  </template>
                  <div class="jdt__span jdt__actions">
                    <el-button
                      size="small"
                      type="primary"
                      :loading="savingRimborsi === r.id"
                      @click="salvaRimborsi(r)"
                    >
                      Salva rimborsi
                    </el-button>
                  </div>
                </div>
              </el-tab-pane>

              <!-- Foto (set PARTENZA/ARRIVO) e allegati stanno sulla tratta.
                   ⚠️ `lazy`: la galleria nasce quando si apre la scheda. Le
                   immagini si caricano solo se sono in vista quando nascono
                   (o al primo scorrimento): montate nella scheda chiusa
                   restavano riquadri grigi finché non si scorreva la pagina. -->
              <el-tab-pane
                name="foto"
                lazy
                :label="`Foto e allegati${contaMedia(r) ? ` (${contaMedia(r)})` : ''}`"
              >
                <MediaGallery
                  :groups="media[r.id]?.groups || []"
                  :attachments="media[r.id]?.attachments || []"
                  :loading="!media[r.id] || media[r.id].loading"
                  :error="media[r.id]?.error || ''"
                  editable
                  :uploading="!!media[r.id]?.uploading"
                  :max-mb="MAX_ALLEGATO_MB"
                  empty-text="Nessuna foto o allegato per questa tratta."
                  @upload="(f) => caricaAllegato(r.id, f)"
                  @remove="(a) => eliminaAllegato(r.id, a)"
                />
              </el-tab-pane>
            </el-tabs>
          </div>
        </section>

        <p
          v-if="!routes.length && !loading && !giornata.aperta"
          class="jdt__card jdt__cardBody jdt__empty"
        >
          Questo spostamento non ha tratte attive.
        </p>

        <!-- In fondo, la giornata: le sedi e gli spostamenti delle auto. -->
        <MappaSpostamenti
          :mappa="mappa"
          class="jdt__giri"
        />
      </div>

      <!-- ================= MODIFICA DI UNA TRATTA ================= -->
      <!-- Si modifica la tratta da sola: driver, minuti, rimborsi, foto e
           note restano. Il backend ricalcola km, prezzo e conferma, e avvisa
           il driver assegnato. -->
      <el-dialog
        v-model="modifica.aperto"
        :title="`Modifica tratta ${modifica.n}`"
        width="600px"
        append-to-body
      >
        <div class="jdt__form">
          <span class="jdt__plRole">Tipo</span>
          <div class="jdt__field">
            <el-radio-group
              v-model="modifica.singolo"
              size="small"
            >
              <el-radio-button :label="true">
                Singolo · driver e jolly
              </el-radio-button>
              <el-radio-button :label="false">
                A catena · solo driver
              </el-radio-button>
            </el-radio-group>
          </div>

          <span class="jdt__plRole">Partenza</span>
          <div class="jdt__field">
            <input
              v-model="modifica.oraDa"
              type="time"
              class="jdt__ora"
              aria-label="Orario di partenza"
              @change="spostaArrivo"
            >
            <el-select
              v-model="modifica.da"
              filterable
              size="small"
              class="jdt__plSel"
              aria-label="Luogo di partenza"
            >
              <el-option
                v-for="o in opzioniLuogo(modifica.route?.startPoint, modifica.route?.start_headquarter_id)"
                :key="o.value"
                :label="o.label"
                :value="o.value"
              />
            </el-select>
          </div>

          <span class="jdt__plRole">Arrivo</span>
          <div class="jdt__field">
            <input
              v-model="modifica.oraA"
              type="time"
              class="jdt__ora"
              aria-label="Orario di arrivo"
            >
            <el-select
              v-model="modifica.a"
              filterable
              size="small"
              class="jdt__plSel"
              aria-label="Luogo di arrivo"
            >
              <el-option
                v-for="o in opzioniLuogo(modifica.route?.endPoint, modifica.route?.end_headquarter_id)"
                :key="o.value"
                :label="o.label"
                :value="o.value"
              />
            </el-select>
          </div>

          <span class="jdt__plRole">Veicolo</span>
          <div class="jdt__field">
            <el-input
              v-model="modifica.marca"
              size="small"
              placeholder="Marca"
              class="jdt__veic"
            >
              <template #prepend>
                Marca
              </template>
            </el-input>
            <el-input
              v-model="modifica.modello"
              size="small"
              placeholder="Modello"
              class="jdt__veic"
            >
              <template #prepend>
                Modello
              </template>
            </el-input>
            <el-input
              v-model="modifica.targa"
              size="small"
              :placeholder="modifica.prova ? 'Targa (facoltativa)' : 'Targa'"
              class="jdt__veic"
            >
              <template #prepend>
                Targa
              </template>
            </el-input>
          </div>

          <span class="jdt__plRole">Cambio</span>
          <div class="jdt__field">
            <el-radio-group
              v-model="modifica.cambio"
              size="small"
            >
              <el-radio-button label="">
                Non indicato
              </el-radio-button>
              <el-radio-button label="MANUAL">
                Manuale
              </el-radio-button>
              <el-radio-button label="AUTOMATIC">
                Automatico
              </el-radio-button>
            </el-radio-group>
          </div>

          <span />
          <div class="jdt__field">
            <el-checkbox v-model="modifica.prova">
              Veicolo non immatricolato (targa prova)
            </el-checkbox>
          </div>

          <p
            v-for="a in avvisiModifica"
            :key="a"
            class="jdt__hint jdt__span"
          >
            {{ a }}
          </p>
        </div>
        <template #footer>
          <el-button @click="modifica.aperto = false">
            Annulla
          </el-button>
          <el-button
            type="primary"
            :loading="modifica.salvando"
            @click="salvaModifica"
          >
            Salva modifiche
          </el-button>
        </template>
      </el-dialog>

      <!-- ================= RIEPILOGO ================= -->
      <aside class="jdt__aside">
        <section class="jdt__card">
          <header class="jdt__cardHead">
            <h3>Riepilogo</h3>
          </header>
          <div class="jdt__cardBody">
            <div class="jdt__kv">
              <span class="jdt__k">Partner</span>
              <span class="jdt__v">{{ partnerName || '—' }}</span>
            </div>
            <div class="jdt__kv">
              <span class="jdt__k">Data</span>
              <span class="jdt__v">{{ dLabel(journey.date) }}</span>
            </div>
            <div class="jdt__kv">
              <span class="jdt__k">Tratte</span>
              <span class="jdt__v">{{ routes.length }}</span>
            </div>
            <div class="jdt__kv">
              <span class="jdt__k">Km totali</span>
              <span class="jdt__v">{{ kmTotali }}</span>
            </div>
            <!-- Quello che resta da fare a Simba: tratte senza le persone. -->
            <div class="jdt__kv">
              <span class="jdt__k">Da assegnare</span>
              <span
                class="jdt__v"
                :class="{ 'jdt__v--attn': daAssegnare.length }"
              >{{ daAssegnare.length ? `Tratte ${daAssegnare.join(', ')}` : 'Nessuna' }}</span>
            </div>
            <div class="jdt__kv">
              <span class="jdt__k">Rientri</span>
              <span
                class="jdt__v"
                :class="{ 'jdt__v--attn': senzaRientro }"
              >{{ senzaRientro ? `${senzaRientro} driver senza rientro` : 'Tutti a posto' }}</span>
            </div>
            <!-- Chi l'ha creato: lo scrive il backend da chi era collegato.
                 Uno spostamento creato da Simba non ha nessun dipendente. -->
            <div class="jdt__kv">
              <span class="jdt__k">Creato da</span>
              <span class="jdt__v">{{ personName(journey.employee) || 'Simba' }}</span>
            </div>
            <div
              v-if="journey.modified_by_employee"
              class="jdt__kv"
            >
              <span class="jdt__k">Modificato da</span>
              <span class="jdt__v">{{ personName(journey.modified_by_employee) }}</span>
            </div>
          </div>
        </section>
      </aside>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import journeyAPI from '../../services/journeyAPI.js';
import driverAPI from '../../services/driverAPI.js';
import contabilitaAPI from '../../services/contabilitaAPI.js';
import partnerAPI from '../../services/partnerAPI.js';
import StatusPill from '../../components/ui/StatusPill.vue';
import ServiceChip from '../../components/ui/ServiceChip.vue';
import MediaGallery from '../../components/ui/MediaGallery.vue';
import { MAX_ALLEGATO_MB, leggiComeDataUri, troppoGrande } from '../../services/fileUpload.js';
import MappaSpostamenti from '../../components/MappaSpostamenti.vue';
import JourneyLegsEditor from '../../components/JourneyLegsEditor.vue';
import { analizzaCatene, chiaveLuogo, corseDaSpostamento, rientriInParole, vistaSedi } from '../../utils/catene';

// Stato della singola tratta -> pill, stesso vocabolario della board.
function routeState(status) {
  if (status === 'CONCLUSA') return { kind: 'muted', label: 'Conclusa' };
  if (status === 'CONFERMATA') return { kind: 'ok', label: 'Confermata' };
  if (status === 'MODIFICATO') return { kind: 'attn', label: 'Modificata' };
  if (status === 'ELIMINATA') return { kind: 'danger', label: 'Eliminata' };
  return { kind: 'active', label: 'Programmata' };
}

// Stato di esecuzione (`route.execution_status`): lo aggiorna l'app driver
// durante la corsa, null finché il driver non parte.
const EXECUTION_LABELS = {
  IN_PARTENZA: { kind: 'active', label: 'Driver in partenza' },
  IN_VIAGGIO: { kind: 'active', label: 'Driver in viaggio' },
  ARRIVATO: { kind: 'ok', label: 'Driver arrivato' },
  CONSEGNATO: { kind: 'ok', label: 'Veicolo consegnato' },
};
const routeExecution = (status) => EXECUTION_LABELS[status] || null;

// Gli stati della tratta come UNA sequenza, come sui booking. `exec: true` =
// stato dell'avanzamento (`route.execution_status`, PATCH …/execution); gli
// altri sono la pianificazione (`route.status`, PATCH …/status). ELIMINATA
// non c'è: ha il suo pulsante «Elimina corsa».
const ROUTE_STATES = [
  { value: 'PROGRAMMATO', label: 'Programmata' },
  { value: 'MODIFICATO', label: 'Modificata' },
  { value: 'CONFERMATA', label: 'Confermata' },
  { value: 'IN_PARTENZA', label: 'Driver in partenza', exec: true },
  { value: 'IN_VIAGGIO', label: 'Driver in viaggio', exec: true },
  { value: 'ARRIVATO', label: 'Driver arrivato', exec: true },
  { value: 'CONSEGNATO', label: 'Veicolo consegnato', exec: true },
  { value: 'CONCLUSA', label: 'Conclusa' },
];
const isExecState = (v) => !!ROUTE_STATES.find((o) => o.value === v)?.exec;

// Lo stato UNICO di una tratta. Conclusa vince su tutto; altrimenti, se il
// driver è partito, conta a che punto è; altrimenti la pianificazione.
const currentState = (r) => {
  if (r.status === 'CONCLUSA') return 'CONCLUSA';
  return r.execution_status || r.status || 'PROGRAMMATO';
};
const statePill = (r) => {
  const v = currentState(r);
  return isExecState(v) ? routeExecution(v) : routeState(v);
};
// Confermare o far partire una corsa senza driver si può, ma va detto.
const needsDriver = (v) => v === 'CONFERMATA' || isExecState(v);


const place = (p) =>
  p ? [p.address, p.street_number, p.cap, p.city, p.province]
    .filter(Boolean).join(' ').replace(/\s+/g, ' ').trim() : '';

export default {
  name: 'JourneyDetailsPage',
  components: { StatusPill, ServiceChip, MediaGallery, MappaSpostamenti, JourneyLegsEditor },

  setup() {
    const route = useRoute();
    const router = useRouter();
    const id = route.params.id;

    const journey = ref({});
    const drivers = ref([]);
    const loading = ref(false);
    const savingRoute = ref('');
    const statusRoute = ref('');
    // Il caricamento dell'«Elimina corsa» si accende solo quando si elimina,
    // non a ogni cambio di stato della stessa tratta.
    const deletingRoute = ref(false);
    // bozza per tratta: { driverId, jollyDriverId, extraMinDriver, extraMinJolly, status }
    const drafts = reactive({});
    // La scheda aperta di ogni tratta (equipaggio, rimborsi, foto).
    const schede = reactive({});
    // Le note come le si sta scrivendo, per tratta.
    const note = reactive({});
    const savingNote = ref('');

    // In ordine di orario: il backend restituisce le tratte nell'ordine di
    // inserimento.
    const routes = computed(() =>
      (journey.value.routes || [])
        .filter((r) => r.status !== 'ELIMINATA')
        .slice()
        .sort((a, b) => new Date(a.dateStart) - new Date(b.dateStart))
    );

    // Rientro a bordo: la tratta (numero) da cui arriva il passeggero di `r`,
    // e quella con cui rientra il driver di `r`. 0 = nessuna.
    // Rientri a bordo: le tratte (numeri) i cui driver rientrano con `r`.
    const daChiRientra = (r) =>
      routes.value.map((x, k) => (x.rientro_route_id === r.id ? k + 1 : 0)).filter(Boolean);
    const passeggeroDa = (r) => daChiRientra(r)[0] || 0;
    // Andata a bordo: le tratte (numeri) i cui driver arrivano a bordo di `r`
    // e poi ne guidano un'altra (1 auto va, ne tornano 2).
    const chiArriva = (r) =>
      routes.value.map((x, k) => (x.andata_route_id === r.id ? k + 1 : 0)).filter(Boolean);
    // Le catene della giornata (utils/catene.js, stesse regole del backend):
    // per il disegno, per «come rientra il driver» e per capire i ritorni.
    const corseSalvate = computed(() =>
      corseDaSpostamento({ ...(journey.value || {}), routes: routes.value })
    );
    const mappa = computed(() => vistaSedi(corseSalvate.value));
    const analisi = computed(() => analizzaCatene(corseSalvate.value));
    const rientri = computed(() => rientriInParole(corseSalvate.value));
    // RITORNO: tratta a catena che chiude la sua catena, o con cui rientra a
    // bordo qualcuno, o che ha già passeggeri. Solo lì si scrivono.
    const eRitorno = (r) =>
      r.two_operators === false &&
      (analisi.value.stato.get(r.id) === 'chiude' ||
        daChiRientra(r).length > 0 ||
        chiArriva(r).length > 0 ||
        (r.passengers || []).length > 0);
    // Come rientra il driver, in schema: `come` (il tipo di rientro),
    // `dove` (con quale tratta o verso dove), `aBordo` (chi rientra con lui).
    const breve = (p) => (p ? [p.address, p.street_number].filter(Boolean).join(' ') || p.city || '' : '');
    const numeroTratta = (rid) => routes.value.findIndex((x) => x.id === rid) + 1;
    const rientroDi = (r) => {
      const a = analisi.value;
      // La partenza della catena del driver: chi è arrivato a bordo di
      // un'altra tratta è partito con quella.
      const chiave = a.origine(r.id);
      const base = routes.value.find((x) => x.id === a.radice.get(r.id) && chiaveLuogo(x.startPoint) === chiave)
        || routes.value.find((x) => chiaveLuogo(x.startPoint) === chiave)
        || routes.value.find((x) => x.id === a.radice.get(r.id));
      const aBordo = (a.passeggeri.get(r.id) || []).map(numeroTratta).filter(Boolean);
      const vanno = (a.andate.get(r.id) || []).map(numeroTratta).filter(Boolean);
      const arriva = (a.andate.get(r.andata_route_id) || []).includes(r.id) ? numeroTratta(r.andata_route_id) : 0;
      const out = {
        come: '', dove: '', attn: false,
        aBordo: aBordo.length ? `${aBordo.length > 1 ? 'Driver delle tratte' : 'Driver della tratta'} ${aBordo.join(', ')}` : '',
        // Chi porta all'andata: guideranno quelle tratte.
        vanno: vanno.length ? `${vanno.length > 1 ? 'I driver delle tratte' : 'Il driver della tratta'} ${vanno.join(', ')}` : '',
        arriva: arriva ? `Passeggero della tratta ${arriva}` : '',
      };
      if (r.two_operators !== false) {
        if (r.rientro_altrove) return { ...out, come: 'Mezzi pubblici', dove: 'Niente jolly' };
        // Singolo che prosegue una catena: il jolly va a prenderlo.
        if (a.padre.get(r.id) && base) {
          return { ...out, come: 'Con il jolly', dove: `${breve(r.endPoint)} → ${breve(base.startPoint)}` };
        }
        return { ...out, come: 'Con il jolly', dove: 'Viaggiano insieme' };
      }
      const stato = a.stato.get(r.id);
      if (stato === 'chiude') return { ...out, come: 'Chiude la catena', dove: `Torna a ${breve(base?.startPoint) || 'la partenza'}` };
      if (stato === 'prosegue') return { ...out, come: 'Prosegue', dove: `Guida la tratta ${numeroTratta(a.figlio.get(r.id))}` };
      if (stato === 'a_bordo') {
        const n = numeroTratta(r.rientro_route_id);
        const t = routes.value.find((x) => x.id === r.rientro_route_id);
        // Col jolly di un singolo che prosegue una catena.
        if (t && t.two_operators !== false) {
          return { ...out, come: 'Con il jolly', dove: `Della tratta ${n}, che lo riporta a ${breve(base?.startPoint) || 'la base'}` };
        }
        return { ...out, come: 'A bordo', dove: n ? `Passeggero della tratta ${n}` : 'Passeggero di un altro spostamento' };
      }
      if (a.andateNonValide.has(r.id)) {
        return { ...out, come: 'Senza andata', dove: `Non arriva a bordo: ${a.andateNonValide.get(r.id)}`, attn: true };
      }
      return { ...out, come: 'Senza rientro', dove: `Resta a ${breve(r.endPoint) || 'destinazione'}`, attn: true };
    };
    // Il riepilogo: km, tratte ancora senza persone, driver senza rientro.
    const kmTotali = computed(() => {
      const km = routes.value.reduce((t, r) => t + (Number(r.km) || 0), 0);
      return `${km.toFixed(2).replace('.', ',')} km`;
    });
    const daAssegnare = computed(() =>
      routes.value
        .map((r, k) => {
          const serveJolly = r.two_operators !== false && !r.rientro_altrove;
          return !r.driver || (serveJolly && !r.jolly_driver) ? k + 1 : 0;
        })
        .filter(Boolean)
    );
    const senzaRientro = computed(() => analisi.value.scoperte.length);
    // Ritorno a catena con altri driver a bordo (o che ne avrà): chi guida è
    // il conducente.
    const conPasseggeri = (r) =>
      r.two_operators === false &&
      (daChiRientra(r).length > 0 || chiArriva(r).length > 0 || (r.passengers || []).length > 0 ||
        (drafts[r.id]?.passengerIds || []).length > 0);
    // Scelto il conducente del ritorno, i passeggeri sono gli ALTRI driver
    // arrivati: quello della corsa da cui riparte e quelli delle corse che
    // rientrano a bordo (come sono scritti ora nella pagina). Si possono
    // correggere a mano.
    const compilaPasseggeri = (r, conducente) => {
      if (!eRitorno(r)) return;
      const padre = routes.value.find((x) => x.id === analisi.value.padre.get(r.id));
      // ...e quelli che all'andata viaggiano a bordo per guidare un'altra tratta.
      const arrivano = [
        padre,
        ...routes.value.filter((x) => x.rientro_route_id === r.id || x.andata_route_id === r.id),
      ].filter(Boolean);
      const driverDi = (x) => drafts[x.id]?.driverId || x.driver?.id || '';
      const altri = [...new Set(arrivano.map(driverDi).filter((id) => id && id !== conducente))];
      if (!altri.length && !daChiRientra(r).length && !chiArriva(r).length) return;
      drafts[r.id].passengerIds = altri.slice(0, 4);
    };
    // Un singolo col jolly che riporta anche altri driver (o che ne ha già).
    const passeggeriSingolo = (r) =>
      r.two_operators !== false &&
      !drafts[r.id]?.rientroAltrove &&
      (daChiRientra(r).length > 0 || (r.passengers || []).length > 0);
    const rientraSu = (r) =>
      r.rientro_route_id ? routes.value.findIndex((x) => x.id === r.rientro_route_id) + 1 : 0;

    const shortId = computed(() => (journey.value.id || '').slice(0, 8));
    const partnerName = computed(() => journey.value.partner?.company_name || '');
    // Nome e cognome di un dipendente, vuoto se non c'è (creato da Simba).
    const personName = (p) => (p ? `${p.name || ''} ${p.surname || ''}`.trim() : '');

    // Stato-giornata: il più "arretrato" comanda, come nella board.
    const dayState = computed(() => {
      const st = routes.value.map((r) => r.status);
      if (!st.length) return { kind: 'muted', label: '—' };
      if (st.every((s) => s === 'CONCLUSA')) return { kind: 'muted', label: 'Concluso' };
      if (st.every((s) => s === 'CONFERMATA' || s === 'CONCLUSA')) {
        return { kind: 'ok', label: 'Confermato' };
      }
      return { kind: 'attn', label: 'Da confermare' };
    });

    // I campi modificabili di una corsa, come stanno salvati.
    const testo = (v) => (v === null || v === undefined ? '' : String(v));
    // Rimborsi come stanno sulla tratta (vuoto = nessuno, o calcolo).
    const rimborsiSalvati = (rt) => ({
      trasfertaDriver: testo(rt.trasferta_driver),
      trasfertaDriverPagata: !!rt.trasferta_driver_pagata,
      trasfertaJolly: testo(rt.trasferta_jolly),
      trasfertaJollyPagata: !!rt.trasferta_jolly_pagata,
      rimborsoKm: testo(rt.rimborso_km_jolly),
    });
    const equipaggioSalvato = (rt) => ({
      driverId: rt.driver?.id || '',
      jollyDriverId: rt.jolly_driver?.id || '',
      passengerIds: (rt.passengers || []).map((x) => x.driverId || x.driver?.id).filter(Boolean),
      rientroAltrove: !!rt.rientro_altrove,
      // Zero e "nessun minuto aggiuntivo" sono la stessa cosa: il campo
      // resta vuoto invece di mostrare uno 0 che sembra un dato scritto.
      extraMinDriver: rt.extra_min_driver ? String(rt.extra_min_driver) : '',
      extraMinJolly: rt.extra_min_jolly ? String(rt.extra_min_jolly) : '',
      state: currentState(rt),
    });

    // `salvata` è la corsa appena salvata, che riparte da quanto dice il
    // server. Sulle altre resta ciò che si stava compilando: ogni Salva vale
    // per una corsa sola, e senza questo le scelte fatte sulle altre corse
    // sparivano dalla pagina senza essere state salvate.
    const load = async (salvata = null) => {
      loading.value = true;
      try {
        const campi = [
          [drafts, equipaggioSalvato],
          [rimborsi, rimborsiSalvati],
          [note, (rt) => rt.details || ''],
        ];
        const inCompilazione = campi.map(([bozze, salvato]) => new Set(
          (journey.value.routes || [])
            .filter((rt) => rt.id !== salvata && bozze[rt.id] !== undefined
              && JSON.stringify(bozze[rt.id]) !== JSON.stringify(salvato(rt)))
            .map((rt) => rt.id),
        ));
        const r = await journeyAPI.getJourneyById(id);
        journey.value = r.data || {};
        for (const rt of journey.value.routes || []) {
          if (!schede[rt.id]) schede[rt.id] = 'equipaggio';
          campi.forEach(([bozze, salvato], i) => {
            if (!inCompilazione[i].has(rt.id)) bozze[rt.id] = salvato(rt);
          });
        }
      } catch (e) {
        console.error('Spostamento non caricato:', e);
        ElMessage.error('Spostamento interno non trovato');
      } finally {
        loading.value = false;
      }
    };

    // Foto e allegati per tratta: { [routeId]: { groups, attachments, loading, error } }.
    // Caricati una volta sola all'apertura: dopo un cambio di stato o di
    // assegnazione non cambiano, e gli URL pre-firmati restano validi.
    const media = reactive({});
    // Chi ha inserito ogni tratta: chiamata a parte (vedi journeyAPI).
    const routeAuthors = ref({});
    const PHASE_LABELS = { PARTENZA: 'Partenza', ARRIVO: 'Arrivo' };

    const loadRouteMedia = async (routeId) => {
      media[routeId] = { groups: [], attachments: [], loading: true, error: '' };
      const [ph, att] = await Promise.allSettled([
        journeyAPI.getRoutePhotos(routeId),
        journeyAPI.getRouteAttachments(routeId),
      ]);
      const m = media[routeId];
      const failures = [];
      const why = (what, e) => {
        const st = e?.response?.status;
        const msg = e?.response?.data?.message;
        const detail = Array.isArray(msg) ? msg.join(', ') : msg;
        return `${what}${st ? ` (errore ${st}${detail ? `: ${detail}` : ''})` : ''}`;
      };
      if (ph.status === 'fulfilled') {
        m.groups = (Array.isArray(ph.value) ? ph.value : [])
          .slice()
          .sort((a, b) => (a.phase === 'PARTENZA' ? -1 : 1) - (b.phase === 'PARTENZA' ? -1 : 1))
          .map((set) => ({
            key: set.id,
            title: `${PHASE_LABELS[set.phase] || set.phase} · foto del driver`,
            urls: Object.values(set.urls || {}).filter(Boolean),
          }))
          .filter((g) => g.urls.length);
      } else {
        console.warn('Foto della tratta non caricate:', ph.reason?.message);
        failures.push(why('le foto', ph.reason));
      }
      if (att.status === 'fulfilled') {
        m.attachments = Array.isArray(att.value) ? att.value : [];
      } else {
        console.warn('Allegati della tratta non caricati:', att.reason?.message);
        failures.push(why('gli allegati', att.reason));
      }
      if (failures.length) m.error = `Non è stato possibile caricare ${failures.join(' e ')}.`;
      m.loading = false;
    };

    // Allegati della tratta caricati da qui (DDT, moduli, foto extra).
    const rileggiAllegati = async (routeId) => {
      try {
        media[routeId].attachments = await journeyAPI.getRouteAttachments(routeId);
      } catch (e) {
        console.warn('Allegati della tratta non riletti:', e?.message);
      }
    };
    const caricaAllegato = async (routeId, file) => {
      if (troppoGrande(file)) {
        ElMessage.warning(`Il file supera i ${MAX_ALLEGATO_MB} MB.`);
        return;
      }
      media[routeId].uploading = true;
      try {
        const dataUri = await leggiComeDataUri(file);
        await journeyAPI.uploadRouteAttachment(routeId, { file: dataUri, fileName: file.name });
        ElMessage.success('Allegato caricato.');
        await rileggiAllegati(routeId);
      } catch (e) {
        // il messaggio del server lo mostra l'interceptor di API.js
        console.error('Allegato non caricato:', e?.response?.status, e?.message);
      } finally {
        media[routeId].uploading = false;
      }
    };
    const eliminaAllegato = async (routeId, allegato) => {
      try {
        await ElMessageBox.confirm(`Eliminare «${allegato.file_name}»?`, 'Elimina allegato', {
          confirmButtonText: 'Elimina', cancelButtonText: 'Annulla', type: 'warning',
        });
      } catch {
        return;
      }
      try {
        await journeyAPI.deleteRouteAttachment(routeId, allegato.id);
        ElMessage.success('Allegato eliminato.');
        await rileggiAllegati(routeId);
      } catch (e) {
        console.error('Allegato non eliminato:', e?.response?.status);
      }
    };

    const contaMedia = (r) => {
      const m = media[r.id];
      if (!m) return 0;
      return (m.groups || []).reduce((t, g) => t + g.urls.length, 0) + (m.attachments || []).length;
    };

    const loadRouteAuthors = async () => {
      try {
        routeAuthors.value = await journeyAPI.getRouteAuthors(id);
      } catch (e) {
        console.warn('Autori delle tratte non caricati:', e?.message);
      }
    };

    const loadDrivers = async () => {
      try {
        const res = await driverAPI.getDriversPaged({ limit: 200, status: 'attivi' });
        drivers.value = res.rows;
      } catch (e) {
        console.warn('Elenco driver non caricato:', e?.message);
      }
    };

    // ------------------------------------------------------------------
    //  Chi trova il driver quando arriva
    //
    //  I dipendenti del partner sono legati alle SEDI, e ogni tratta sa da
    //  quale sede parte e in quale arriva. Sapere chi c'è dall'altra parte
    //  serve a chi smista: se il driver arriva e non trova nessuno, è a
    //  quella persona che si telefona.
    //
    //  Una chiamata sola per tutto lo spostamento, e la mappa si costruisce
    //  qui: le sedi di una tratta sono al massimo due, i dipendenti pochi.
    // ------------------------------------------------------------------
    const employeesByHq = ref({});

    const loadEmployees = async () => {
      const partnerId = journey.value?.partner?.id;
      if (!partnerId) return;
      try {
        const elenco = await partnerAPI.getEmployeesByPartner(partnerId);
        const mappa = {};
        for (const e of elenco) {
          const nome = [e.name, e.surname].filter(Boolean).join(' ').trim();
          if (!nome) continue;
          for (const h of e.headquarters || []) {
            if (!h?.id) continue;
            (mappa[h.id] = mappa[h.id] || []).push(nome);
          }
        }
        employeesByHq.value = mappa;
      } catch (e) {
        console.warn('Dipendenti delle sedi non caricati:', e?.message);
      }
    };

    /** I nomi dei dipendenti di una sede, o '' se la sede non è nota. */
    const referentiSede = (headquarterId) =>
      (employeesByHq.value[headquarterId] || []).join(', ');

    // Minuti aggiuntivi: numero intero ≥ 0, campo vuoto = zero. Si mandano
    // sempre, così togliendoli si azzerano davvero.
    const minuti = (v) => {
      const n = Number(String(v ?? '').replace(',', '.').trim() || 0);
      return Number.isFinite(n) && n >= 0 ? Math.round(n) : null;
    };

    // ---- Rimborsi della tratta (stessi campi del COD6) -----------------------
    const rimborsi = reactive({});
    const savingRimborsi = ref('');
    // Il rimborso km che il COD6 calcola da sé: km × tariffa dell'agenzia del
    // jolly. È il suggerimento del campo vuoto.
    const rimborsoKmCalcolato = (r) => {
      const jolly = drivers.value.find((d) => d.id === r.jolly_driver?.id);
      const tariffa = Number(jolly?.agency?.km_refund_rate) || 0;
      const km = Number(r.km) || 0;
      return tariffa && km ? `calcolo € ${(km * tariffa).toFixed(2).replace('.', ',')}` : 'dal calcolo';
    };
    const salvaRimborsi = async (r) => {
      const bozza = rimborsi[r.id];
      const leggi = (v) => {
        const t = String(v ?? '').trim().replace(',', '.');
        if (!t) return null;
        const n = Number(t);
        return Number.isFinite(n) && n >= 0 ? n : NaN;
      };
      const tDriver = leggi(bozza.trasfertaDriver);
      const tJolly = leggi(bozza.trasfertaJolly);
      const km = leggi(bozza.rimborsoKm);
      if ([tDriver, tJolly, km].some((n) => Number.isNaN(n))) {
        ElMessage.error('I rimborsi devono essere importi, non negativi (es. 12,50)');
        return;
      }
      savingRimborsi.value = r.id;
      try {
        if (r.driver) {
          await contabilitaAPI.cod6SetVociCorsa({
            kind: 'route', id: r.id, role: 'driver', trasferta: tDriver, pagata: bozza.trasfertaDriverPagata,
          });
        }
        if (r.jolly_driver) {
          await contabilitaAPI.cod6SetVociCorsa({
            kind: 'route', id: r.id, role: 'jolly', trasferta: tJolly, pagata: bozza.trasfertaJollyPagata, rimborsoKm: km,
          });
        }
        ElMessage.success('Rimborsi salvati.');
        await load(r.id);
      } catch (e) {
        ElMessage.error(e?.response?.data?.message || 'Rimborsi non salvati');
      } finally {
        savingRimborsi.value = '';
      }
    };

    const saveAssignment = async (r) => {
      const extraMinDriver = minuti(drafts[r.id].extraMinDriver);
      const extraMinJolly = minuti(drafts[r.id].extraMinJolly);
      if (extraMinDriver === null || extraMinJolly === null) {
        ElMessage.error('I minuti aggiuntivi devono essere un numero di minuti, non negativo');
        return;
      }
      savingRoute.value = r.id;
      try {
        const res = await journeyAPI.assignDrivers(id, {
          assignments: [{
            routeId: r.id,
            driverId: drafts[r.id].driverId || null,
            // Il jolly c'è solo sulle singole; i passeggeri solo sui ritorni
            // a catena (assente = invariato).
            jollyDriverId:
              r.two_operators !== false && !drafts[r.id].rientroAltrove ? drafts[r.id].jollyDriverId || null : undefined,
            rientroAltrove: r.two_operators !== false ? !!drafts[r.id].rientroAltrove : undefined,
            passengerIds: eRitorno(r) || passeggeriSingolo(r) ? drafts[r.id].passengerIds || [] : undefined,
            extraMinDriver,
            extraMinJolly,
          }],
        });
        ElMessage.success('Assegnazione salvata.');
        // Il backend assegna comunque, ma dice se quel driver è già
        // impegnato in quelle ore (altra tratta o prenotazione). L'avviso
        // resta finché non lo si chiude: è una cosa da guardare, non un
        // messaggio che sparisce da solo.
        for (const c of res?.data?.conflitti ?? []) {
          ElMessage({ type: 'warning', message: c.messaggio, duration: 0, showClose: true });
        }
        await load(r.id);
      } catch (e) {
        console.error('Assegnazione non riuscita:', e);
        ElMessage.error('Assegnazione non riuscita');
      } finally {
        savingRoute.value = '';
      }
    };

    // Errore di un cambio di stato: il backend spiega il rifiuto.
    const statusError = (e) => {
      const msg = e?.response?.data?.message;
      ElMessage.error(
        e?.response?.status === 403
          ? 'Non hai i permessi per cambiare lo stato di questa tratta'
          : (Array.isArray(msg) ? msg.join(', ') : msg) || 'Cambio stato non riuscito',
      );
    };

    // Il campo è uno, le colonne due: qui si decide quale muovere perché lo
    // stato letto dopo (`currentState`) sia proprio quello scelto.
    //  - stato del driver: la corsa deve risultare confermata (una conclusa
    //    va riaperta, una programmata si conferma), poi si segna l'avanzamento;
    //  - Conclusa: basta la pianificazione, che vince su tutto;
    //  - Programmata / Modificata / Confermata: si toglie l'avanzamento, se il
    //    driver risultava partito, e si scrive la pianificazione.
    const saveState = async (r) => {
      const next = drafts[r.id].state;
      if (!next || next === currentState(r)) return;
      statusRoute.value = r.id;
      try {
        if (isExecState(next)) {
          if (r.status !== 'CONFERMATA') await journeyAPI.setRouteStatus(r.id, 'CONFERMATA');
          await journeyAPI.setRouteExecution(r.id, next);
        } else {
          if (next !== 'CONCLUSA' && r.execution_status) {
            await journeyAPI.setRouteExecution(r.id, null);
          }
          if (next !== r.status) await journeyAPI.setRouteStatus(r.id, next);
        }
        const label = ROUTE_STATES.find((o) => o.value === next)?.label || next;
        ElMessage.success(`Stato aggiornato: ${label.toLowerCase()}.`);
      } catch (e) {
        console.error('Cambio stato non riuscito:', e);
        statusError(e);
      } finally {
        // Si ricarica anche dopo un errore: se è fallito il secondo passo, il
        // primo è andato e la pagina deve mostrare lo stato vero.
        await load(r.id);
        statusRoute.value = '';
      }
    };

    const salvaNota = async (r) => {
      savingNote.value = r.id;
      try {
        const res = await journeyAPI.setRouteNote(r.id, (note[r.id] || '').trim());
        // Solo la nota: niente ricarica, il resto della pagina non cambia.
        r.details = res.data?.details ?? '';
        note[r.id] = r.details;
        ElMessage.success('Nota salvata.');
      } catch (e) {
        const msg = e?.response?.data?.message;
        ElMessage.error((Array.isArray(msg) ? msg.join(', ') : msg) || 'Nota non salvata');
      } finally {
        savingNote.value = '';
      }
    };

    // ---- Modifica di una tratta -------------------------------------------
    // Le sedi del partner, per cambiare partenza e arrivo.
    const sedi = ref([]);
    const loadSedi = async () => {
      const pid = journey.value?.partner?.id;
      if (!pid) return;
      try {
        const res = await partnerAPI.getPartnerDetail(pid);
        sedi.value = res.data?.headquarters || [];
      } catch (e) {
        console.warn('Sedi del partner non caricate:', e?.message);
      }
    };
    const due = (n) => String(n).padStart(2, '0');
    const oraDi = (d) => {
      const x = new Date(d);
      return Number.isNaN(x.getTime()) ? '' : `${due(x.getHours())}:${due(x.getMinutes())}`;
    };
    const minutiDi = (hhmm) => {
      const [h, m] = String(hhmm || '').split(':').map(Number);
      return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
    };
    // Un luogo: 'hq:<id>' per una sede del partner, 'attuale' per il punto
    // che c'è già quando non è una sede.
    const valoreLuogo = (hqId) => (hqId && sedi.value.some((s) => s.id === hqId) ? `hq:${hqId}` : 'attuale');
    const opzioniLuogo = (punto, hqId) => [
      ...(valoreLuogo(hqId) === 'attuale' ? [{ value: 'attuale', label: place(punto) || '—' }] : []),
      ...sedi.value.map((s) => ({ value: `hq:${s.id}`, label: `${s.name || 'Sede'} — ${place(s)}` })),
    ];
    const puntoDi = (valore) => {
      const s = sedi.value.find((x) => `hq:${x.id}` === valore);
      if (!s) return null;
      const testo = (v) => (v === null || v === undefined ? '' : String(v));
      const p = {
        headquarter_id: s.id,
        address: testo(s.address),
        street_number: testo(s.street_number),
        cap: testo(s.cap),
        city: testo(s.city),
        province: testo(s.province),
      };
      if (Number.isFinite(Number(s.latitude)) && s.latitude !== null) p.latitude = Number(s.latitude);
      if (Number.isFinite(Number(s.longitude)) && s.longitude !== null) p.longitude = Number(s.longitude);
      return p;
    };

    const modifica = reactive({
      aperto: false, salvando: false, route: null, n: 0, durata: 0,
      singolo: true, da: '', a: '', oraDa: '', oraA: '',
      marca: '', modello: '', targa: '', cambio: '', prova: false,
    });
    const apriModifica = (r, i) => {
      Object.assign(modifica, {
        aperto: true,
        salvando: false,
        route: r,
        n: i + 1,
        durata: Math.max(1, Math.round((new Date(r.dateEnd) - new Date(r.dateStart)) / 60000) || 1),
        singolo: r.two_operators !== false,
        da: valoreLuogo(r.start_headquarter_id),
        a: valoreLuogo(r.end_headquarter_id),
        oraDa: oraDi(r.dateStart),
        oraA: oraDi(r.dateEnd),
        marca: r.marca || '',
        modello: r.modello || '',
        targa: r.targa || '',
        cambio: r.gearbox_type || '',
        prova: r.immatricolato === false,
      });
    };
    // Spostando la partenza, l'arrivo segue con la stessa durata (se resta
    // nella giornata); si può comunque correggere a mano.
    const spostaArrivo = () => {
      const da = minutiDi(modifica.oraDa);
      if (da === null) return;
      const a = da + modifica.durata;
      if (a < 24 * 60) modifica.oraA = `${due(Math.floor(a / 60))}:${due(a % 60)}`;
    };
    const avvisiModifica = computed(() => {
      const r = modifica.route;
      if (!r) return [];
      const out = [];
      const eraSingolo = r.two_operators !== false;
      if (eraSingolo && !modifica.singolo) {
        out.push(r.jolly_driver
          ? 'A catena il jolly non serve: quello assegnato viene tolto.'
          : 'A catena il driver resta a destinazione: controlla con quale corsa rientra.');
      }
      if (!eraSingolo && modifica.singolo) {
        out.push('Singolo: serve anche il jolly. Finché non lo assegni la tratta resta da confermare.');
      }
      if (modifica.da !== valoreLuogo(r.start_headquarter_id) || modifica.a !== valoreLuogo(r.end_headquarter_id)) {
        out.push('Cambiando sede si ricalcolano km e prezzo; controlla l\'orario di arrivo.');
      }
      if (r.driver || r.jolly_driver) out.push('Chi è assegnato riceve l\'avviso della modifica.');
      return out;
    });
    const salvaModifica = async () => {
      const r = modifica.route;
      if (!r) return;
      const da = minutiDi(modifica.oraDa);
      const a = minutiDi(modifica.oraA);
      if (da === null || a === null) {
        ElMessage.error('Indica gli orari di partenza e di arrivo');
        return;
      }
      if (a <= da) {
        ElMessage.error("L'arrivo deve essere dopo la partenza (la tratta non può passare la mezzanotte)");
        return;
      }
      if (!modifica.prova && !modifica.targa.trim()) {
        ElMessage.error('Manca la targa (o segna la targa prova)');
        return;
      }
      const alle = (hhmm) => {
        const d = new Date(r.dateStart);
        const [h, m] = hhmm.split(':').map(Number);
        d.setHours(h, m, 0, 0);
        return d.toISOString();
      };
      const dto = {};
      if (modifica.singolo !== (r.two_operators !== false)) dto.two_operators = modifica.singolo;
      if (modifica.oraDa !== oraDi(r.dateStart) || modifica.oraA !== oraDi(r.dateEnd)) {
        dto.dateStart = alle(modifica.oraDa);
        dto.dateEnd = alle(modifica.oraA);
      }
      if (modifica.da !== valoreLuogo(r.start_headquarter_id)) dto.startPoint = puntoDi(modifica.da);
      if (modifica.a !== valoreLuogo(r.end_headquarter_id)) dto.endPoint = puntoDi(modifica.a);
      if (modifica.marca.trim() !== (r.marca || '')) dto.marca = modifica.marca.trim();
      if (modifica.modello.trim() !== (r.modello || '')) dto.modello = modifica.modello.trim();
      if (modifica.targa.trim() !== (r.targa || '')) dto.targa = modifica.targa.trim();
      if (modifica.cambio !== (r.gearbox_type || '')) dto.gearbox_type = modifica.cambio;
      if (modifica.prova !== (r.immatricolato === false)) dto.immatricolato = !modifica.prova;
      if (!Object.keys(dto).length) {
        modifica.aperto = false;
        ElMessage.info('Nessuna modifica da salvare.');
        return;
      }
      modifica.salvando = true;
      try {
        const res = await journeyAPI.updateRoute(r.id, dto);
        ElMessage.success('Tratta modificata.');
        for (const c of res?.data?.conflitti ?? []) {
          ElMessage({ type: 'warning', message: c.messaggio, duration: 0, showClose: true });
        }
        modifica.aperto = false;
        await load(r.id);
      } catch (e) {
        const msg = e?.response?.data?.message;
        ElMessage.error((Array.isArray(msg) ? msg.join(', ') : msg) || 'Modifica non salvata');
      } finally {
        modifica.salvando = false;
      }
    };

    // ---- Modifica completa dello spostamento --------------------------------
    // Il modulo del partner, seminato con le corse salvate (ognuna col suo id:
    // il backend le aggiorna sul posto). Le corse tolte passano ELIMINATA
    // solo se il salvataggio parte.
    const legsEditor = ref(null);
    const giornata = reactive({
      aperta: false, salvando: false, forza: false, sessione: 0, corse: [], daEliminare: [],
    });
    const giornoIso = computed(() => (journey.value.date
      ? new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Rome' }).format(new Date(journey.value.date))
      : ''));
    // Alla stessa ora prima l'auto con la targa: l'auto «principale» resta quella.
    const ordineCorse = (a, b) =>
      (new Date(a.dateStart) - new Date(b.dateStart))
      || (Number(!a.targa) - Number(!b.targa))
      || String(a.targa || '').localeCompare(String(b.targa || ''))
      || String(a.id || '').localeCompare(String(b.id || ''));
    const testoLuogo = (p, hqId) => {
      const s = sedi.value.find((x) => x.id === hqId);
      return s ? `${s.name} — ${place(p)}` : place(p);
    };
    const corseEditor = () => (journey.value.routes || [])
      .filter((r) => r.movement !== false && r.status !== 'ELIMINATA')
      .slice()
      .sort(ordineCorse)
      .map((r) => ({
        sourceId: r.id,
        // La sede resta la sede (`id` = sede nell'editor), così chi la vede
        // fra i dipendenti del partner non cambia.
        startPoint: r.startPoint ? { ...r.startPoint, id: r.start_headquarter_id || undefined } : null,
        endPoint: r.endPoint ? { ...r.endPoint, id: r.end_headquarter_id || undefined } : null,
        startText: r.startPoint ? testoLuogo(r.startPoint, r.start_headquarter_id) : '',
        endText: r.endPoint ? testoLuogo(r.endPoint, r.end_headquarter_id) : '',
        dateStart: oraDi(r.dateStart),
        dateEnd: oraDi(r.dateEnd),
        details: r.details || '',
        marca: r.marca || '',
        modello: r.modello || '',
        targa: r.targa || '',
        gearbox_type: r.gearbox_type || '',
        nonImmatricolato: r.immatricolato === false,
        // Il prezzo deciso a mano resta nel campo: salvando non si perde.
        prezzoManuale: r.imponibile_manuale === null || r.imponibile_manuale === undefined
          ? ''
          : Number(r.imponibile_manuale).toFixed(2).replace('.', ','),
        isShuttle: r.two_operators !== false,
        autoChained: false,
        rientroSourceId: r.rientro_route_id || null,
        andataSourceId: r.andata_route_id || null,
      }));
    const apriModificaGiornata = () => {
      Object.assign(giornata, { aperta: true, forza: false, corse: corseEditor(), daEliminare: [] });
      giornata.sessione += 1;
    };
    const chiudiModificaGiornata = () => {
      giornata.aperta = false;
      giornata.daEliminare = [];
    };
    const salvaModificaGiornata = async () => {
      const editor = legsEditor.value;
      if (!editor) return;
      // Prima il controllo del modulo, poi le eliminazioni: una corsa tolta
      // non deve sparire se il salvataggio poi si ferma.
      const check = editor.validate({ ignoreAvailability: giornata.forza });
      if (!check.ok) {
        ElMessage.error(check.message);
        return;
      }
      giornata.salvando = true;
      try {
        for (const rid of giornata.daEliminare) await journeyAPI.setRouteStatus(rid, 'ELIMINATA');
        giornata.daEliminare = [];
        const payload = editor.buildJourneyPayload({ id: journey.value.partner?.id });
        if (giornata.forza) payload.ignore_availability = true;
        const res = await journeyAPI.updateJourney(id, payload);
        ElMessage.success('Spostamento modificato.');
        for (const c of res?.data?.conflitti ?? []) {
          ElMessage({ type: 'warning', message: c.messaggio, duration: 0, showClose: true });
        }
        giornata.aperta = false;
        await load();
        loadRouteAuthors();
        for (const r of routes.value) if (!media[r.id]) loadRouteMedia(r.id);
      } catch (e) {
        const msg = e?.response?.data?.message;
        ElMessage.error((Array.isArray(msg) ? msg.join(', ') : msg) || 'Modifica non salvata');
      } finally {
        giornata.salvando = false;
      }
    };

    // Dal menu della pastiglia. Confermare o far partire una tratta senza
    // driver si può, ma lo si chiede.
    const cambiaStato = async (r, v) => {
      if (!v || v === currentState(r)) return;
      if (needsDriver(v) && !r.driver) {
        try {
          await ElMessageBox.confirm(
            'La tratta non ha ancora un driver salvato. Cambiare lo stato lo stesso?',
            'Tratta senza driver',
            { confirmButtonText: 'Cambia stato', cancelButtonText: 'Annulla', type: 'warning' },
          );
        } catch {
          return;
        }
      }
      drafts[r.id].state = v;
      await saveState(r);
    };

    // Eliminare avvisa il driver e toglie la corsa da questa pagina: non deve
    // partire con un clic distratto.
    const deleteRoute = async (r) => {
      try {
        await ElMessageBox.confirm(
          'La corsa sparirà da questo spostamento e il driver assegnato riceverà l\'avviso di annullamento.',
          'Eliminare la corsa?',
          { confirmButtonText: 'Elimina', cancelButtonText: 'Annulla', type: 'warning' },
        );
      } catch {
        return;
      }
      statusRoute.value = r.id;
      deletingRoute.value = true;
      try {
        await journeyAPI.setRouteStatus(r.id, 'ELIMINATA');
        ElMessage.success('Corsa eliminata.');
        await load();
      } catch (e) {
        console.error('Eliminazione non riuscita:', e);
        statusError(e);
      } finally {
        statusRoute.value = '';
        deletingRoute.value = false;
      }
    };

    const hhmm = (d) =>
      d ? new Date(d).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '—';
    const dLabel = (d) =>
      d ? new Date(d).toLocaleDateString('it-IT', {
        weekday: 'short', day: 'numeric', month: 'long', year: 'numeric',
      }) : '—';

    const goBack = () => router.push('/booking');

    onMounted(async () => {
      loadDrivers();
      loadRouteAuthors();
      await load();
      // Dopo `load`: il partner lo si sa solo a spostamento caricato.
      loadEmployees();
      loadSedi();
      for (const r of routes.value) loadRouteMedia(r.id);
    });

    return {
      caricaAllegato, eliminaAllegato, MAX_ALLEGATO_MB,
      journey, drivers, loading, savingRoute, statusRoute, drafts,
      ROUTE_STATES, currentState, statePill, cambiaStato, deleteRoute, deletingRoute, schede, contaMedia,
      rientroDi, kmTotali, daAssegnare, senzaRientro, note, savingNote, salvaNota,
      modifica, apriModifica, opzioniLuogo, spostaArrivo, avvisiModifica, salvaModifica,
      legsEditor, giornata, giornoIso, apriModificaGiornata, chiudiModificaGiornata, salvaModificaGiornata, sedi, id,
      media, routes, passeggeroDa, rientraSu, daChiRientra, chiArriva, eRitorno, passeggeriSingolo, conPasseggeri, compilaPasseggeri, mappa, rientri, shortId,
      rimborsi, savingRimborsi, rimborsoKmCalcolato, salvaRimborsi, partnerName, personName, dayState, routeAuthors,
      place, saveAssignment,
      hhmm, dLabel, goBack, referentiSede,
    };
  },
};
</script>

<style scoped>
.jdt { padding: 4px 0 32px; }
.jdt__spacer { flex: 1; }

.jdt__head {
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin: 0 2px 16px;
}
.jdt__back {
  width: 36px; height: 36px; flex: none; border-radius: 9px;
  border: 1px solid var(--border-color); background: var(--bg-primary);
  display: grid; place-items: center; cursor: pointer;
  font-size: 16px; color: var(--text-secondary); font-family: inherit;
}
.jdt__back:hover { border-color: var(--brand-violet); }
.jdt__title {
  margin: 0; font-size: 21px; display: flex; align-items: center;
  gap: 10px; flex-wrap: wrap; color: var(--text-primary);
}
.jdt__sub {
  margin-top: 4px; display: flex; align-items: center; gap: 8px;
  flex-wrap: wrap; font-size: 13px; color: var(--text-tertiary);
}
.jdt__id { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }

.jdt__grid {
  display: grid; grid-template-columns: 1fr 330px; gap: 16px; align-items: start;
}
@media (max-width: 960px) { .jdt__grid { grid-template-columns: 1fr; } }
.jdt__col { display: flex; flex-direction: column; gap: 16px; }
.jdt__aside {
  position: sticky; top: 16px; display: flex; flex-direction: column; gap: 16px;
}
@media (max-width: 960px) { .jdt__aside { position: static; } }

.jdt__card {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
}
.jdt__cardHead {
  display: flex; align-items: center; flex-wrap: wrap; gap: 8px 10px;
  padding: 13px 16px; border-bottom: 1px solid var(--border-color);
}
.jdt__cardHead > * { flex: none; white-space: nowrap; }
.jdt__cardHead > .jdt__spacer { flex: 1; }
.jdt__cardHead h3 { margin: 0; font-size: 14px; color: var(--text-primary); }
.jdt__cardBody { padding: 16px; }
.jdt__empty { font-size: 13px; color: var(--text-tertiary); }

.jdt__tag2op {
  font-size: 11px; font-weight: 700; border-radius: 5px; padding: 1px 7px;
  color: var(--svc-fleet); background: var(--svc-fleet-bg);
}

.jdt__prova {
  font-size: 10.5px; font-weight: 700; padding: 1px 7px; border-radius: 5px;
  color: #92400e; background: #fef3c7;
}
.jdt__stateBtn {
  display: inline-flex; align-items: center; gap: 4px; padding: 2px 4px;
  border: 0; border-radius: 999px; background: none; cursor: pointer; font: inherit;
}
.jdt__stateBtn:hover, .jdt__stateBtn:focus-visible { background: var(--bg-tertiary); outline: none; }
.jdt__caret { font-size: 11px; color: var(--text-tertiary); }

/* Panoramica: il viaggio a sinistra, il rientro a destra. */
.jdt__overview { display: flex; flex-wrap: wrap; gap: 16px; }
.jdt__trip { flex: 1 1 320px; display: flex; flex-direction: column; gap: 10px; min-width: 0; }
.jdt__route {
  display: grid; grid-template-columns: auto 9px minmax(0, 1fr);
  align-items: center; gap: 6px 10px;
}
.jdt__time {
  font-size: 15px; font-weight: 750; font-variant-numeric: tabular-nums; color: var(--text-primary);
}
.jdt__time--arr { color: var(--text-secondary); }
.jdt__pin {
  width: 9px; height: 9px; border-radius: 50%;
  border: 2px solid var(--text-tertiary); background: transparent;
}
.jdt__pin--to { background: var(--text-tertiary); }
.jdt__addr { font-size: 13.5px; color: var(--text-primary); overflow-wrap: anywhere; }
.jdt__who { font-size: 12.5px; color: var(--text-secondary); }

.jdt__veh { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; font-size: 13px; }
.jdt__vehCar { font-weight: 600; color: var(--text-primary); }
.jdt__vehPlate {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px; font-weight: 600; background: var(--bg-tertiary);
  border: 1px solid var(--border-color); border-radius: 5px; padding: 1px 7px;
}
.jdt__dim { font-size: 13px; color: var(--text-secondary); }
.jdt__facts {
  display: flex; flex-wrap: wrap; gap: 4px 14px;
  font-size: 13px; color: var(--text-secondary); font-variant-numeric: tabular-nums;
}

.jdt__rientro {
  flex: 1 0 200px; max-width: 280px;
  margin: 0; display: flex; flex-direction: column; gap: 2px; align-self: start;
  padding: 10px 12px; border-radius: 8px; background: var(--bg-secondary, var(--bg-tertiary));
}
.jdt__rientro dd { margin: 0; }
.jdt__rientroCome { font-size: 14px; font-weight: 700; color: var(--text-primary); }
.jdt__rientroDove { font-size: 13px; color: var(--text-secondary); overflow-wrap: anywhere; }
.jdt__k--sub { margin-top: 8px; }
.jdt__rientro--attn { background: rgba(185, 28, 28, 0.07); }
.jdt__rientro--attn .jdt__rientroCome { color: var(--state-err, #b91c1c); }

/* Note: si scrivono qui. Piene o vuote, stessa cornice discreta. */
.jdt__notes {
  margin-top: 14px; padding: 10px 12px; border-radius: 8px;
  display: flex; flex-direction: column; gap: 6px;
  border: 1px solid var(--border-color); background: transparent;
}
.jdt__notesIn :deep(.el-textarea__inner) {
  font-size: 13.5px; line-height: 1.5; box-shadow: none; background: transparent;
  margin-left: -11px; resize: none; overflow: hidden;
}
.jdt__notesIn :deep(.el-textarea__inner:focus) {
  background: var(--bg-primary); box-shadow: 0 0 0 1px var(--brand-violet) inset;
}
.jdt__notesAct { display: flex; justify-content: flex-end; gap: 6px; }

.jdt__tabs { margin-top: 12px; }
.jdt__form {
  display: grid; grid-template-columns: 110px minmax(0, 1fr); align-items: center; gap: 10px 12px;
}
.jdt__span { grid-column: 1 / -1; }
.jdt__field { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.jdt__plRole {
  font-size: 11px; font-weight: 700; letter-spacing: 0.03em;
  text-transform: uppercase; color: var(--text-secondary);
}
.jdt__plRole--jolly { color: var(--svc-fleet); }
.jdt__plSel { flex: 1; min-width: 200px; max-width: 360px; }
.jdt__plMin { width: 150px; flex: none; }
.jdt__plKm { width: 200px; flex: none; }
.jdt__actions { display: flex; justify-content: flex-end; }
.jdt__editActions {
  display: flex; align-items: center; flex-wrap: wrap; gap: 10px; margin-top: 16px;
}
.jdt__ora {
  height: 24px; padding: 0 6px; flex: none;
  border: 1px solid var(--border-color); border-radius: 4px;
  font: inherit; font-size: 13px; font-variant-numeric: tabular-nums;
  color: var(--text-primary); background: var(--bg-primary);
}
.jdt__ora:focus-visible { outline: 2px solid var(--brand-violet); outline-offset: 1px; }
.jdt__veic { width: 200px; flex: none; }
.jdt__hint { margin: 0; font-size: 12px; line-height: 1.5; color: var(--text-tertiary); }
.jdt__giri { margin-top: 0; }

.jdt__kv {
  display: flex; justify-content: space-between; gap: 12px;
  padding: 7px 0; border-bottom: 1px solid var(--border-color);
  font-size: 13px;
}
.jdt__kv:last-child { border-bottom: 0; }
.jdt__k {
  font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
  color: var(--text-tertiary);
}
.jdt__kv .jdt__k { font-size: 13px; font-weight: 400; letter-spacing: 0; text-transform: none; }
.jdt__v { font-weight: 600; color: var(--text-primary); text-align: right; }
.jdt__v--attn { color: var(--state-err, #b91c1c); }
</style>
